/**
 * Shared, best-effort browser address resolution for patient-controlled
 * address forms and provisional Institution intake. This helper never writes
 * an address; callers decide whether and when an authorised save can occur.
 */

export const ADDRESS_LOCATION_UX_BUDGET_MS = 3000;
export const ADDRESS_GEOLOCATION_OPTIONS = Object.freeze({
  enableHighAccuracy: false,
  maximumAge: 300000,
  timeout: 1600,
});

function abortError() {
  const error = new Error("Address lookup aborted");
  error.name = "AbortError";
  return error;
}

const COARSE_RESULT_GRACE_MS = 320;

function text(value) {
  return String(value || "").trim();
}

function firstText(...values) {
  return values.map(text).find(Boolean) || "";
}

function structuredAddress({ streetAddress = "", city = "", region = "", postalCode = "", country = "" } = {}) {
  return {
    streetAddress: text(streetAddress),
    city: text(city),
    region: text(region),
    postalCode: text(postalCode),
    country: text(country),
  };
}

function validAddress(value) {
  return Boolean(value?.streetAddress || value?.city || value?.region || value?.postalCode || value?.country);
}

export function addressQuality(value) {
  if (!validAddress(value)) return 0;
  return (value.streetAddress ? 5 : 0)
    + (value.city ? 4 : 0)
    + (value.postalCode ? 3 : 0)
    + (value.country ? 2 : 0)
    + (value.region ? 1 : 0);
}

function isPreciseAddress(value) {
  return Boolean(value?.streetAddress && value?.city && value?.postalCode && value?.country);
}

export function mapNominatimAddress(json) {
  const address = json?.address || {};
  const street = firstText(address.road, address.street, address.pedestrian, address.footway, address.residential);
  return structuredAddress({
    streetAddress: [text(address.house_number), street].filter(Boolean).join(" "),
    city: firstText(address.city, address.town, address.village, address.municipality, address.locality),
    region: firstText(address.state, address.canton, address.province, address.state_district),
    postalCode: address.postcode,
    country: address.country,
  });
}

export function mapBigDataCloudAddress(json) {
  const administrative = Array.isArray(json?.localityInfo?.administrative) ? json.localityInfo.administrative : [];
  const administrativeLocality = administrative.find((item) => /city|town|village|municipality|locality/i.test(`${item?.type || ""} ${item?.adminLevel || ""} ${item?.description || ""}`));
  const road = firstText(json?.street, json?.road, json?.streetName);
  return structuredAddress({
    streetAddress: [firstText(json?.houseNumber, json?.house_number), road].filter(Boolean).join(" "),
    city: firstText(json?.locality, json?.city, administrativeLocality?.name),
    region: json?.principalSubdivision,
    postalCode: json?.postcode,
    country: json?.countryName,
  });
}

function connectAbortSignal(controller, signal) {
  if (!signal) return () => {};
  if (signal.aborted) {
    controller.abort();
    return () => {};
  }
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  return () => signal.removeEventListener("abort", abort);
}

export function getBrowserPosition({
  geolocation = typeof navigator === "undefined" ? null : navigator.geolocation,
  signal,
  options = ADDRESS_GEOLOCATION_OPTIONS,
} = {}) {
  if (!geolocation?.getCurrentPosition) {
    return Promise.reject(new Error("Location is not available in this browser."));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      callback(value);
    };
    const onAbort = () => finish(reject, abortError());

    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
    geolocation.getCurrentPosition(
      (position) => finish(resolve, position),
      (error) => finish(reject, error),
      options,
    );
  });
}

/**
 * Concurrently probes the approved public reverse-geocode providers. The
 * highest-quality usable response wins and aborts every remaining request.
 */
export async function reverseGeocodeAddress(
  latitude,
  longitude,
  {
    signal,
    timeoutMs = ADDRESS_LOCATION_UX_BUDGET_MS,
    fetchImpl = typeof fetch === "undefined" ? null : fetch,
  } = {},
) {
  if (typeof fetchImpl !== "function") throw new Error("Address lookup is unavailable.");

  const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;
  const bigDataCloudUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
  const candidates = [
    {
      url: nominatimUrl,
      parse: mapNominatimAddress,
    },
    {
      url: bigDataCloudUrl,
      parse: mapBigDataCloudAddress,
    },
  ];

  const controller = new AbortController();
  const disconnect = connectAbortSignal(controller, signal);
  const timeout = setTimeout(() => controller.abort(), Math.max(1, timeoutMs));
  const aborted = new Promise((_, reject) => {
    controller.signal.addEventListener("abort", () => reject(abortError()), { once: true });
  });

  try {
    const deadlineAt = Date.now() + Math.max(1, timeoutMs);
    const pending = new Set(candidates.map((candidate) => {
      const entry = { candidate };
      entry.promise = Promise.resolve()
        .then(() => fetchImpl(candidate.url, { headers: { "Accept-Language": "en" }, signal: controller.signal }))
        .then(async (response) => {
          if (!response?.ok) throw new Error("Resolver unavailable");
          const value = candidate.parse(await response.json());
          if (!validAddress(value)) throw new Error("Resolver returned no usable address");
          return { entry, value };
        })
        .catch(() => ({ entry, value: null }));
      return entry;
    }));

    let best = null;
    let coarseDeadline = null;
    while (pending.size) {
      const stopAt = Math.min(deadlineAt, coarseDeadline || deadlineAt);
      const waitMs = Math.max(1, stopAt - Date.now());
      const timeoutResult = new Promise((resolve) => setTimeout(() => resolve(null), waitMs));
      const result = await Promise.race([Promise.race([...pending].map(({ promise }) => promise)), timeoutResult, aborted]);
      if (!result) break;
      pending.delete(result.entry);
      if (!result.value) continue;
      if (!best || addressQuality(result.value) > addressQuality(best)) best = result.value;
      if (isPreciseAddress(best)) return best;
      if (!coarseDeadline) coarseDeadline = Math.min(deadlineAt, Date.now() + COARSE_RESULT_GRACE_MS);
    }
    if (best) return best;
    throw new Error("Resolver returned no usable address");
  } catch {
    throw new Error("Address lookup failed");
  } finally {
    clearTimeout(timeout);
    disconnect();
    controller.abort();
  }
}

/**
 * Bounds the complete browser-position plus reverse-geocode interaction to
 * the UX budget. Callers can cancel an older attempt by aborting `signal`.
 */
export async function resolveCurrentAddress({
  geolocation,
  signal,
  timeoutMs = ADDRESS_LOCATION_UX_BUDGET_MS,
  reverseGeocode = reverseGeocodeAddress,
} = {}) {
  const controller = new AbortController();
  const disconnect = connectAbortSignal(controller, signal);
  const startedAt = Date.now();
  const deadline = setTimeout(() => controller.abort(), Math.max(1, timeoutMs));

  try {
    const position = await getBrowserPosition({ geolocation, signal: controller.signal });
    const remainingMs = timeoutMs - (Date.now() - startedAt);
    if (remainingMs <= 0) throw abortError();
    const address = await reverseGeocode(position.coords.latitude, position.coords.longitude, {
      signal: controller.signal,
      timeoutMs: remainingMs,
    });
    return { coords: position.coords, address };
  } finally {
    clearTimeout(deadline);
    disconnect();
    controller.abort();
  }
}
